import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { storyGroupDtoSchema, type StoryGroupDto } from '@tg-hookah/shared/dto';
import { api } from '@/lib/api';

const listSchema = z.array(storyGroupDtoSchema);

async function fetchStoryGroups(): Promise<StoryGroupDto[]> {
  const res = await api.get('/stories');
  return listSchema.parse(res.data);
}

export function useStories() {
  return useQuery({
    queryKey: ['stories'],
    queryFn: fetchStoryGroups,
    // Stories change often (admin adds them live). Refetch every minute and on
    // tab focus. Server-side Redis caches the feed for 60s so polling is cheap.
    staleTime: 15_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

/** Marks a story group viewed (server-side), optimistically flipping its
 *  `isViewed` flag in the cache so the ring greys out immediately. */
export function useMarkStoryGroupViewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (groupId: string): Promise<void> => {
      await api.post(`/stories/${groupId}/view`);
    },
    onMutate: (groupId: string) => {
      qc.setQueryData<StoryGroupDto[]>(['stories'], (prev) =>
        prev?.map((g) => (g.id === groupId ? { ...g, isViewed: true } : g)),
      );
    },
  });
}
